import {
    ComponentProps,
    MouseEvent,
    PropsWithChildren,
    createContext,
    forwardRef,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@givebutter/primitives/dialog';
import { useToast } from '@givebutter/primitives/toast';
import { brandColors, productColors } from '@givebutter/util';
import api from '@flatfile/api';
import { FlatfileListener } from '@flatfile/listener';
import { recordHook } from '@flatfile/plugin-record-hook';
import { FlatfileProvider, useSpace } from '@flatfile/react';
import { faCheck, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as PortalPrimitive from '@radix-ui/react-portal';
import { Slot } from '@radix-ui/react-slot';
import {
    DraftImportRequestParams,
    ImportRequestParams,
    createImport as createImportApi,
    createImportDraft,
} from 'api/flatfile';
import * as ImportDialog from 'components/imports/ImportDialog';
import { ImportStrategyContext } from 'components/imports/context';
import { useImportStrategy } from 'components/imports/importStrategy';
import { ImportStrategy } from 'components/imports/types';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';
import { useTheme } from 'helpers/themes';
import { accountQueryKeys } from 'queries/account-query-keys';
import { ImportType } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface FlatfileButtonProps {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    strategy: ImportType;
}

interface IImportContext {
    showSpace: boolean;
    setShowSpace: (show: boolean) => void;
    savedDraftId: number | null;
    setSavedDraftId: (id: number | null) => void;
}

const ImportContext = createContext<IImportContext>({
    showSpace: false,
    setShowSpace: () => {},
    savedDraftId: null,
    setSavedDraftId: () => {},
});

const Import = ({
    open = false,
    onOpenChange,
    strategy,
    children,
}: PropsWithChildren<FlatfileButtonProps>) => {
    const [showSpace, setShowSpace] = useState(open);
    const [savedDraftId, setSavedDraftId] = useState<number | null>(null);

    const importStrategy = useImportStrategy(strategy);

    // to update the internal `showSpace` state when `open` was changed externally.
    useEffect(() => {
        setShowSpace(open);
    }, [open, setShowSpace]);

    const value = useMemo(() => {
        const setShowSpaceFn = (show: boolean) => {
            setShowSpace(show);
            onOpenChange?.(show);
        };

        return {
            showSpace,
            setShowSpace: setShowSpaceFn,
            strategy,
            savedDraftId,
            setSavedDraftId,
        };
    }, [onOpenChange, showSpace, setShowSpace, strategy, savedDraftId, setSavedDraftId]);

    const importStrategyObject = useMemo(() => ({ importStrategy }), [importStrategy]);

    return (
        <ImportContext.Provider value={value}>
            <ImportStrategyContext.Provider value={importStrategyObject}>
                <FlatfileProvider
                    publishableKey={import.meta.env.VITE_DASHBOARD_FLATFILE_PUBLISHABLE_KEY}
                >
                    {children}
                </FlatfileProvider>
            </ImportStrategyContext.Provider>
        </ImportContext.Provider>
    );
};

type TriggerProps = ComponentProps<'button'> & {
    asChild?: boolean;
};

const ImportTrigger = forwardRef<HTMLButtonElement, TriggerProps>(
    ({ children, asChild = false, ...props }, forwardedRef) => {
        const { setShowSpace } = useContext(ImportContext);
        const Component = asChild ? Slot : 'button';

        return (
            <Component
                {...props}
                onClick={event => {
                    setShowSpace(true);
                    props.onClick?.(event as MouseEvent<HTMLButtonElement>);
                }}
                ref={forwardedRef}
            >
                {children}
            </Component>
        );
    },
);

const spaceProps = {
    name: 'Dashboard embed',
    publishableKey: import.meta.env.VITE_DASHBOARD_FLATFILE_PUBLISHABLE_KEY,
    environmentId: import.meta.env.VITE_DASHBOARD_FLATFILE_ENV_ID,
};

const ImportSpace = () => {
    const toast = useToast();
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const { setShowSpace, showSpace, savedDraftId } = useContext(ImportContext);
    const { importStrategy } = useContext(ImportStrategyContext);

    if (!showSpace) {
        return null;
    }

    return (
        <Dialog.Root
            open={showSpace}
            onOpenChange={open => {
                if (savedDraftId) {
                    toast.success('Import saved as draft.');
                    history.push(`/accounts/${accountId}/settings/imports/drafts`);
                }

                setShowSpace(open);
            }}
        >
            <Dialog.Portal>
                <Dialog.Overlay />
                <ImportDialog.Content>
                    <Dialog.Close />
                    <ImportDialog.Header>
                        <ImportDialog.Title>Import {importStrategy.displayName}</ImportDialog.Title>
                        <ImportDialog.Draft>
                            {savedDraftId ? (
                                <>
                                    <FontAwesomeIcon icon={faCheck} className={'text-green-600'} />
                                    <p>Draft saved</p>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faTimes} className={'text-red-600'} />
                                    <p>Draft unsaved</p>
                                </>
                            )}
                        </ImportDialog.Draft>
                    </ImportDialog.Header>
                    <ImportDialog.Body>
                        <ImportDialog.HelpCenter />
                        <ImportDialog.SpaceContainer>
                            <ImportSpaceInternal />
                        </ImportDialog.SpaceContainer>
                    </ImportDialog.Body>
                </ImportDialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

interface CreateListenerProps<T = unknown> {
    importStrategy: ImportStrategy;
    mappedFields: Set<string>;
    createImport: UseMutationResult<T, GivebutterAxiosError, ImportRequestParams, unknown>;
    savedDraftId: number | null;
    setShowSpace: (show: boolean) => void;
    setMappedFields: (fields: Set<string>) => void;
    createDraft: UseMutationResult<any, GivebutterAxiosError, DraftImportRequestParams, unknown>;
}

const handleSubmitAction =
    ({
        importStrategy,
        mappedFields,
        createImport,
        setShowSpace,
        savedDraftId,
    }: Pick<
        CreateListenerProps,
        'importStrategy' | 'mappedFields' | 'createImport' | 'setShowSpace' | 'savedDraftId'
    >) =>
    (configure: FlatfileListener) => {
        configure.on('job:ready', async event => {
            const {
                context: { jobId, workbookId, spaceId },
            } = event;

            try {
                const { data: workbook } = await api.workbooks.get(workbookId);
                const sheet = workbook.sheets![0];

                const [{ data: files }, { data: recordCounts }] = await Promise.all([
                    api.files.list({ spaceId }),
                    api.sheets.getRecordCounts(sheet.id),
                ]);

                sheet.config.fields = sheet.config.fields.map(field => ({
                    ...field,
                    ...(mappedFields.has(field.key) ? { metadata: { mapped: true } } : {}),
                }));

                api.workbooks.update(workbook.id, workbook);

                await api.jobs.ack(jobId, {
                    info: 'Getting started.',
                    progress: 10,
                });

                createImport.mutate(
                    {
                        draft_id: savedDraftId ?? undefined,
                        sheet_id: sheet.id,
                        file_id: files.length ? files[0].id : null,
                        workbook_id: workbookId,
                        space_id: spaceId,
                        type: importStrategy.type,
                        rows: recordCounts.counts.total,
                        file_name: files.length ? files[0].name : null,
                        ...(importStrategy.context ?? {}),
                    },
                    {
                        onSuccess: () => {
                            api.jobs.complete(jobId, {
                                outcome: {
                                    acknowledge: true,
                                    message: 'This is now complete.',
                                    next: {
                                        type: 'wait',
                                    },
                                },
                            });

                            setShowSpace(false);
                        },
                        onError: error => {
                            api.jobs.fail(jobId, {
                                outcome: {
                                    acknowledge: true,
                                    message:
                                        error.response?.data.message ??
                                        'An unknown error occurred.',
                                },
                            });
                        },
                    },
                );
            } catch {
                await api.jobs.fail(jobId, {
                    outcome: {
                        message: 'This job encountered an error.',
                    },
                });
            }
        });
    };

const handleWorkbookMapped =
    ({
        importStrategy,
        createDraft,
        savedDraftId,
        mappedFields,
        setMappedFields,
    }: Pick<
        CreateListenerProps,
        'importStrategy' | 'createDraft' | 'savedDraftId' | 'mappedFields' | 'setMappedFields'
    >) =>
    (configure: FlatfileListener) => {
        configure.on('job:completed', async ({ context: { jobId, spaceId } }) => {
            try {
                const jobPlan = await api.jobs.getExecutionPlan(jobId);

                const currentFields = new Set(
                    jobPlan.data.plan.fieldMapping.map(mapping => mapping.destinationField.key),
                );

                setMappedFields(mappedFields.union(currentFields));

                if (!savedDraftId) {
                    createDraft.mutate({
                        space_id: spaceId,
                        type: importStrategy.type,
                        ...(importStrategy.context ?? {}),
                    });
                }
            } catch {
                api.jobs.fail(jobId, {
                    outcome: {
                        message: 'This job encountered an error.',
                    },
                });
            }
        });
    };

const createListener = ({
    importStrategy,
    mappedFields,
    createImport,
    savedDraftId,
    setShowSpace,
    setMappedFields,
    createDraft,
}: CreateListenerProps) =>
    FlatfileListener.create(listener => {
        listener.use(recordHook(importStrategy.type, importStrategy.recordHook));
        listener.filter(
            { job: 'workbook:submitActionFg' },
            handleSubmitAction({
                importStrategy,
                mappedFields,
                createImport,
                setShowSpace,
                savedDraftId,
            }),
        );
        listener.filter(
            { job: 'workbook:map' },
            handleWorkbookMapped({
                importStrategy,
                createDraft,
                savedDraftId,
                mappedFields,
                setMappedFields,
            }),
        );
    });

const ImportSpaceInternal = () => {
    const toast = useToast();
    const history = useHistory();
    const { setShowSpace, savedDraftId, setSavedDraftId } = useContext(ImportContext);
    const { importStrategy } = useContext(ImportStrategyContext);
    const [mappedFields, setMappedFields] = useState<Set<string>>(new Set());
    const queryClient = useQueryClient();

    const { id: userId, email } = useCurrentUser();
    const { accountId, title } = useCurrentAccount();
    const { mutate: invalidateUserData } = useInvalidateUserAndAccount();
    const themeConfig = useThemeInternal();

    const createDraft = useMutation({
        mutationFn: (params: DraftImportRequestParams) => createImportDraft(accountId, params),
        onError: (error: GivebutterAxiosError) => toast.error(error.response?.data.message),
        onSuccess: data => {
            setSavedDraftId(data.id);

            queryClient.invalidateQueries({ queryKey: accountQueryKeys.imports(accountId) });
        },
    });

    const createImport = useMutation({
        mutationFn: (params: ImportRequestParams) => createImportApi(accountId, params),
        onError: (error: GivebutterAxiosError) => toast.error(error.response?.data.message),
        onSuccess: data => {
            history.push(`/accounts/${accountId}/settings/imports/${data.id}`);
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.imports(accountId) });
            invalidateUserData();
        },
    });

    const listener = useMemo<FlatfileListener>(
        () =>
            createListener({
                importStrategy,
                createImport,
                savedDraftId,
                setShowSpace,
                mappedFields,
                setMappedFields,
                createDraft,
            }),
        [
            importStrategy,
            mappedFields,
            createImport,
            savedDraftId,
            setShowSpace,
            setMappedFields,
            createDraft,
        ],
    );

    const useSpaceProps = {
        ...spaceProps,
        workbook: importStrategy.blueprint,
        listener,
        spaceBody: {
            name: importStrategy.blueprint.name,
        },
        displayAsModal: false,
        themeConfig,
        sidebarConfig: {
            showSidebar: true,
        },
        userInfo: {
            userId: userId.toString(),
            name: email,
            companyId: accountId.toString(),
            companyName: title,
        },
        closeSpace: {
            operation: 'submitActionFg',
            onClose: () => {
                if (savedDraftId) {
                    toast.success('Import saved as draft.');
                    history.push(`/accounts/${accountId}/settings/imports/drafts`);
                }

                setShowSpace(false);
            },
        },
    };

    return useSpace(useSpaceProps);
};

const useThemeInternal = () => {
    const theme = useTheme();

    return {
        root: {
            fontFamily: theme.theme.typography.font,
            primaryColor: brandColors.butter.primary,
            successColor: productColors.success[500],
            dangerColor: productColors.error[500],
            warningColor: productColors.warning[500],
        },
        table: {
            fontFamily: theme.theme.typography.font,
        },
        sidebar: {
            logo: 'images/logo-orange.png',
        },
    };
};

const Root = Import;
const Trigger = ImportTrigger;
const Space = ImportSpace;
const ImportPortal = PortalPrimitive.Root;
const Portal = ImportPortal;

export {
    Import,
    ImportTrigger,
    ImportSpace,
    ImportPortal,
    //
    Root,
    Trigger,
    Space,
    Portal,
};
